import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, X, Loader2, UserRound } from "lucide-react";

const empty = { name: "", email: "", password: "", funcao: "", tipo_trabalho: "", phone: "" };

export default function EmployeesPage() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);

  const load = () =>
    api.get("/users").then((r) => setList(r.data)).finally(() => setLoading(false));

  useEffect(() => {
    load();
  }, []);

  const openNew = () => {
    setEditing(null);
    setForm(empty);
    setModal(true);
  };
  const openEdit = (u) => {
    setEditing(u);
    setForm({ ...empty, ...u, password: "" });
    setModal(true);
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editing) {
        const payload = { ...form };
        if (!payload.password) delete payload.password;
        await api.put(`/users/${editing.id}`, payload);
        toast.success("Funcionário atualizado");
      } else {
        await api.post("/users", form);
        toast.success("Funcionário criado");
      }
      setModal(false);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (u) => {
    if (!window.confirm(`Eliminar ${u.name}?`)) return;
    await api.delete(`/users/${u.id}`);
    toast.success("Funcionário eliminado");
    load();
  };

  return (
    <div data-testid="employees-page">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="font-display text-3xl font-extrabold tracking-tight">Funcionários</h1>
          <p className="text-muted-foreground">{list.length} conta(s) registada(s).</p>
        </div>
        <button
          data-testid="add-employee-button"
          onClick={openNew}
          className="flex items-center gap-2 px-4 py-2.5 rounded-full bg-primary text-primary-foreground font-semibold hover:bg-[#F5B05E] transition-colors"
        >
          <Plus className="w-4 h-4" /> Novo funcionário
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center py-16">
          <Loader2 className="w-7 h-7 text-primary animate-spin" />
        </div>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {list.map((u) => (
            <div
              key={u.id}
              data-testid={`employee-card-${u.id}`}
              className="bg-card border border-border rounded-xl p-5 hover:border-primary/40 transition-colors"
            >
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-11 h-11 rounded-full bg-primary/15 flex items-center justify-center">
                    <UserRound className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <p className="font-semibold leading-tight">{u.name}</p>
                    <p className="text-xs text-muted-foreground">{u.email}</p>
                  </div>
                </div>
              </div>
              <div className="mt-4 flex flex-wrap gap-2">
                {u.funcao && (
                  <span className="text-xs px-2 py-1 rounded-md bg-secondary text-secondary-foreground">
                    {u.funcao}
                  </span>
                )}
                {u.tipo_trabalho && (
                  <span className="text-xs px-2 py-1 rounded-md bg-secondary text-muted-foreground">
                    {u.tipo_trabalho}
                  </span>
                )}
              </div>
              <div className="mt-4 flex gap-2">
                <button
                  data-testid={`edit-employee-${u.id}`}
                  onClick={() => openEdit(u)}
                  className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg bg-secondary text-sm hover:bg-secondary/70 transition-colors"
                >
                  <Pencil className="w-3.5 h-3.5" /> Editar
                </button>
                <button
                  data-testid={`delete-employee-${u.id}`}
                  onClick={() => remove(u)}
                  className="px-3 py-2 rounded-lg bg-red-500/10 text-red-400 hover:bg-red-500/20 transition-colors"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {modal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/60" onClick={() => setModal(false)} />
          <form
            onSubmit={save}
            data-testid="employee-form"
            className="relative bg-card border border-border rounded-2xl w-full max-w-md p-6 max-h-[90vh] overflow-y-auto"
          >
            <div className="flex items-center justify-between mb-5">
              <h2 className="font-display text-xl font-bold">
                {editing ? "Editar funcionário" : "Novo funcionário"}
              </h2>
              <button type="button" onClick={() => setModal(false)}>
                <X className="w-5 h-5 text-muted-foreground" />
              </button>
            </div>
            <div className="space-y-3">
              <Field label="Nome" testid="emp-name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} required />
              <Field label="Email" testid="emp-email" type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} required />
              <Field
                label={editing ? "Nova palavra-passe (opcional)" : "Palavra-passe"}
                testid="emp-password"
                type="password"
                value={form.password}
                onChange={(v) => setForm({ ...form, password: v })}
                required={!editing}
              />
              <Field label="Função" testid="emp-funcao" value={form.funcao} onChange={(v) => setForm({ ...form, funcao: v })} placeholder="Ex: Pedreiro" />
              <Field label="Tipo de trabalho" testid="emp-tipo" value={form.tipo_trabalho} onChange={(v) => setForm({ ...form, tipo_trabalho: v })} placeholder="Ex: Alvenaria" />
              <Field label="Telemóvel" testid="emp-phone" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} />
            </div>
            <button
              data-testid="save-employee-button"
              disabled={saving}
              className="w-full mt-6 py-3 rounded-full bg-primary text-primary-foreground font-semibold hover:bg-[#F5B05E] transition-colors flex items-center justify-center gap-2 disabled:opacity-60"
            >
              {saving && <Loader2 className="w-4 h-4 animate-spin" />} Guardar
            </button>
          </form>
        </div>
      )}
    </div>
  );
}

export function Field({ label, value, onChange, type = "text", required, placeholder, testid }) {
  return (
    <div>
      <label className="block text-sm font-medium mb-1.5">{label}</label>
      <input
        data-testid={testid}
        type={type}
        required={required}
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full px-3 py-2.5 rounded-lg bg-input border border-border focus:border-primary focus:ring-1 focus:ring-primary outline-none transition-colors"
      />
    </div>
  );
}
